// Location (design 01c 4): pure helpers for location cards, live shares,
// family places and the "다닌 길" trail map. Nothing here touches the DOM,
// the network or the map SDK, so it is tested on its own.
//
// Coordinates in the API are integers in micro-degrees (lat_e6, lng_e6);
// the map works in degrees (LatLng).
import type { Card, LiveLocation, LocationCard, Motion, Place } from '../../api/types'
import type { LatLng } from '../map/provider'

const E6 = 1_000_000
const MIN_MS = 60_000

// ---- coordinates ----

/** A point from micro-degrees, or null when either is missing. */
export function fromE6(lat: number | null | undefined, lng: number | null | undefined): LatLng | null {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return null
  return { lat: lat / E6, lng: lng / E6 }
}

export function toE6(at: LatLng): { lat_e6: number; lng_e6: number } {
  return {
    lat_e6: clamp(Math.round(at.lat * E6), -90 * E6, 90 * E6),
    lng_e6: clamp(Math.round(at.lng * E6), -180 * E6, 180 * E6),
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n))
}

/** "37.56650, 126.97800": shown when there is no address. */
export function coordText(at: LatLng): string {
  return `${at.lat.toFixed(5)}, ${at.lng.toFixed(5)}`
}

/** Metres between two points (haversine; good enough for a city). */
export function distanceM(a: LatLng, b: LatLng): number {
  const R = 6_371_000
  const rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad
  const dLng = (b.lng - a.lng) * rad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** The visible part of a map, in degrees. */
export interface Bbox {
  south: number
  west: number
  north: number
  east: number
}

/** A box as the trail query wants it: micro-degrees, rounded outwards and
    kept in range. */
export function bboxE6(b: Bbox): { min_lat_e6: number; min_lng_e6: number; max_lat_e6: number; max_lng_e6: number } {
  return {
    min_lat_e6: clamp(Math.floor(Math.min(b.south, b.north) * E6), -90 * E6, 90 * E6),
    min_lng_e6: clamp(Math.floor(Math.min(b.west, b.east) * E6), -180 * E6, 180 * E6),
    max_lat_e6: clamp(Math.ceil(Math.max(b.south, b.north) * E6), -90 * E6, 90 * E6),
    max_lng_e6: clamp(Math.ceil(Math.max(b.west, b.east) * E6), -180 * E6, 180 * E6),
  }
}

/** Whether box a holds box b (a trail answer for a can be reused for b). */
export function bboxContains(a: Bbox, b: Bbox): boolean {
  return a.south <= b.south && a.west <= b.west && a.north >= b.north && a.east >= b.east
}

// ---- time words ----

/** "12분", "1시간 5분", "2시간" */
export function durationText(ms: number): string {
  const m = Math.max(0, Math.floor(ms / MIN_MS))
  if (m < 60) return `${m}분`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return rest > 0 ? `${h}시간 ${rest}분` : `${h}시간`
}

/** "40분째" (how long someone has been in one place). */
export function sinceText(since: number, now: number): string {
  return `${durationText(now - since)}째`
}

/** "12분 남음", "1시간 5분 남음", "곧 끝남" */
export function remainingText(until: number, now: number): string {
  const ms = until - now
  if (ms < MIN_MS) return '곧 끝남'
  return `${durationText(ms)} 남음`
}

/** "방금", "5분 전", "2시간 전" (when a position was measured) */
export function agoText(at: number, now: number): string {
  const ms = Math.max(0, now - at)
  if (ms < MIN_MS) return '방금'
  return `${durationText(ms)} 전`
}

// ---- live shares ----

export const MOTION_LABELS: Record<Motion, string> = {
  unknown: '',
  still: '머무는 중',
  walk: '걷는 중',
  vehicle: '차로 이동 중',
}

/** "시속 4 km" */
export function speedText(kmh: number | null): string {
  if (kmh === null || !Number.isFinite(kmh)) return ''
  return `시속 ${Math.round(Math.max(0, kmh))} km`
}

/** "학교 · 40분째", "이 근처 · 12분째", or '' when not staying. */
export function stayText(live: Pick<LiveLocation, 'stay'>, places: Record<number, Place>, now: number): string {
  const s = live.stay
  if (!s) return ''
  const place = s.place_id !== null ? places[s.place_id] : undefined
  const where = place && !place.deleted ? place.name : '이 근처'
  return `${where} · ${sinceText(s.since, now)}`
}

/** "연락 끊김 · 마지막 12분 전" for a share that went quiet, else ''. */
export function lostText(live: Pick<LiveLocation, 'lost' | 'contact_at' | 'measured_at'>, now: number): string {
  if (!live.lost) return ''
  const last = live.contact_at ?? live.measured_at
  return last ? `연락 끊김 · 마지막 ${agoText(last, now)}` : '연락 끊김'
}

/** The one line under a person on the map and in lists: what they do now. */
export function liveSummary(live: LiveLocation, places: Record<number, Place>, now: number): string {
  const lost = lostText(live, now)
  if (lost) return lost
  const parts: string[] = []
  const stay = stayText(live, places, now)
  if (stay) parts.push(stay)
  else {
    const motion = MOTION_LABELS[live.motion]
    if (motion) parts.push(motion)
    const speed = live.motion !== 'still' ? speedText(live.speed_kmh) : ''
    if (speed) parts.push(speed)
  }
  if (live.lat_e6 === null) parts.push('위치를 기다리는 중')
  else if (live.measured_at) parts.push(agoText(live.measured_at, now))
  return parts.join(' · ')
}

/** A live share's address is asked again (Kakao, on this device: design
    01c 4.2) only when it moved this far from where it was last asked... */
export const LIVE_ADDRESS_MOVE_M = 150
/** ...or this long went by since, so that a day stays well within the
    free quota. */
export const LIVE_ADDRESS_AGE_MS = 3 * MIN_MS

/** Where and when a share's address was last asked. */
export interface AskedAddress {
  at: LatLng
  asked: number
}

/** Whether to ask the address of a live share's new point: the first
    time, then for a new point after LIVE_ADDRESS_MOVE_M or
    LIVE_ADDRESS_AGE_MS (the same point is never asked twice). */
export function addressDue(last: AskedAddress | undefined, at: LatLng, now: number): boolean {
  if (!last) return true
  if (last.at.lat === at.lat && last.at.lng === at.lng) return false
  return distanceM(last.at, at) > LIVE_ADDRESS_MOVE_M || now - last.asked >= LIVE_ADDRESS_AGE_MS
}

/** Whether a live card's share is over (stopped or its time is up). */
export function liveOver(loc: Pick<LocationCard, 'mode' | 'ended_at' | 'live_until'>, now: number): boolean {
  if (loc.mode !== 'live') return true
  return loc.ended_at !== null || (loc.live_until ?? 0) <= now
}

export interface LiveDuration {
  minutes: number
  label: string
}

/** The lengths offered for a live share (the server takes 15 to 480 minutes). */
export const LIVE_DURATIONS: LiveDuration[] = [
  { minutes: 15, label: '15분' },
  { minutes: 30, label: '30분' },
  { minutes: 60, label: '1시간' },
  { minutes: 120, label: '2시간' },
  { minutes: 240, label: '4시간' },
  { minutes: 480, label: '8시간' },
]

/** One of my shares this device should report for. */
export interface MyShare {
  cardId: number
  until: number
}

/** The live shares this device should send my position to: mine, not
    ended, not past their end, not reported by my location helper
    (via_helper), and not among those the server said are over or are
    another device's (stopped, until a newer card arrives). From the live
    store (GET /api/loc/live, loc.live) and the cards I know (a share I just
    started is a card before it is a live entry). Soonest end first. */
export function sharesToReport(
  live: Record<number, LiveLocation>,
  cards: Record<number, Card>,
  meId: number,
  now: number,
  stopped: ReadonlySet<number> = new Set(),
): MyShare[] {
  const out = new Map<number, number>()
  const helpers = new Set(
    Object.values(cards)
      .filter((c) => c.location?.via_helper)
      .map((c) => c.id),
  )
  for (const l of Object.values(live)) {
    if (l.user_id === meId && l.live_until > now && !stopped.has(l.card_id) && !helpers.has(l.card_id))
      out.set(l.card_id, l.live_until)
  }
  for (const c of Object.values(cards)) {
    const loc = c.location
    if (c.deleted || !loc || loc.mode !== 'live' || loc.user_id !== meId || stopped.has(c.id) || loc.via_helper) continue
    if (liveOver(loc, now)) {
      out.delete(c.id) // the card knows it ended even if a live entry is late
      continue
    }
    out.set(c.id, loc.live_until ?? now)
  }
  return [...out.entries()].map(([cardId, until]) => ({ cardId, until })).sort((a, b) => a.until - b.until || a.cardId - b.cardId)
}

/** The shortest time between two reports the server takes (design 01c 6). */
export const REPORT_MIN_S = 15
/** A report goes at least this often, without coordinates when nothing new
    was measured (a heartbeat: "still here"). */
export const HEARTBEAT_S = 60

export interface ReportClock {
  now: number
  /** When the last report went (0: none yet). */
  lastSentAt: number
  /** When this device began watching: the first heartbeat waits a while
      for a position from then. */
  startedAt: number
  /** The interval the server asked for (interval_s), at least REPORT_MIN_S. */
  intervalS: number
  /** measured_at of the newest position the device has, or 0. */
  latestFixAt: number
  /** measured_at of the last position sent, or 0. */
  sentFixAt: number
}

export type ReportDecision = { kind: 'fix' | 'heartbeat' } | { kind: 'wait'; ms: number }

/** What to send now: a new position (at most every interval), a heartbeat
    when nothing new came for HEARTBEAT_S, or how long to wait. */
export function reportDecision(c: ReportClock): ReportDecision {
  const interval = Math.max(REPORT_MIN_S, c.intervalS) * 1000
  const heartbeat = Math.max(HEARTBEAT_S * 1000, interval)
  if (c.lastSentAt > 0 && c.now - c.lastSentAt < interval) return { kind: 'wait', ms: c.lastSentAt + interval - c.now }
  if (c.latestFixAt > c.sentFixAt) return { kind: 'fix' }
  const since = Math.max(c.lastSentAt, c.startedAt)
  if (c.now - since >= heartbeat) return { kind: 'heartbeat' }
  return { kind: 'wait', ms: since + heartbeat - c.now }
}

// ---- places ----

export const PLACE_NAME_MAX = 20
export const PLACE_RADIUS_DEFAULT = 100
export const PLACE_RADIUS_MIN = 30
export const PLACE_RADIUS_MAX = 2000
/** The radii offered when making a place. */
export const PLACE_RADII = [50, 100, 200, 500, 1000]

/** A place name as the server keeps it (trimmed, 1 to 20 letters), or null. */
export function placeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, ' ')
  const n = [...name].length
  return n >= 1 && n <= PLACE_NAME_MAX ? name : null
}

/** "100 m", "1 km", "1.5 km" */
export function radiusText(m: number): string {
  if (m < 1000) return `${m} m`
  const km = m / 1000
  return `${Number.isInteger(km) ? km : km.toFixed(1)} km`
}

/** The family's places by name (Korean order), without deleted ones. */
export function sortedPlaces(places: Record<number, Place>): Place[] {
  return Object.values(places)
    .filter((p) => !p.deleted)
    .sort((a, b) => a.name.localeCompare(b.name, 'ko') || a.id - b.id)
}

/** Keeps a place unless an equal or newer version is there; a deleted one goes. */
export function putPlace(places: Record<number, Place>, p: Place): Record<number, Place> {
  const old = places[p.id]
  if (old && old.version > p.version) return places
  if (p.deleted) {
    if (!old) return places
    const { [p.id]: _gone, ...rest } = places
    return rest
  }
  if (old && old.version === p.version && old.updated_at === p.updated_at) return places
  return { ...places, [p.id]: p }
}

/** The family place a point is in (the nearest whose circle holds it), or null. */
export function placeAt(places: Record<number, Place>, at: LatLng): Place | null {
  let best: Place | null = null
  let bestD = Infinity
  for (const p of Object.values(places)) {
    if (p.deleted) continue
    const d = distanceM(at, { lat: p.lat_e6 / E6, lng: p.lng_e6 / E6 })
    if (d <= p.radius_m && d < bestD) {
      best = p
      bestD = d
    }
  }
  return best
}

// ---- trails ("다닌 길의 굵기", design 01c 4.5) ----

/** How an edge is drawn: often-walked ones thick and dark like synapses.
    width 1..12 px by log(1+w)/log(1+max), opacity 0.25..0.9 by the same. */
export function trailStyle(weight: number, maxWeight: number): { strokeWeight: number; opacity: number } {
  const r = maxWeight > 0 && weight > 0 ? Math.min(1, Math.log1p(weight) / Math.log1p(maxWeight)) : 0
  return { strokeWeight: 1 + 11 * r, opacity: 0.25 + 0.65 * r }
}

// ---- the device's position ----

/** Words for a failed navigator.geolocation call (GeolocationPositionError.code). */
export function geoErrorText(code: number): string {
  switch (code) {
    case 1:
      return '위치 권한이 꺼져 있습니다. 브라우저(또는 휴대폰 설정 → 앱)에서 이 앱의 위치 권한을 허용해 주세요.'
    case 2:
      return '지금 위치를 알 수 없습니다. 휴대폰의 위치(GPS)가 켜져 있는지 보고, 밖이나 창가에서 다시 해 보세요.'
    case 3:
      return '위치를 찾는 데 너무 오래 걸립니다. 잠시 뒤에 다시 해 보세요.'
    default:
      return '이 기기에서는 위치를 쓸 수 없습니다.'
  }
}

/** A measured position as the API wants it. */
export interface Fix {
  lat_e6: number
  lng_e6: number
  accuracy_m: number
  measured_at: number
  speed_mps?: number
}

export function fixOf(p: {
  coords: { latitude: number; longitude: number; accuracy: number; speed: number | null }
  timestamp: number
}): Fix {
  const fix: Fix = {
    ...toE6({ lat: p.coords.latitude, lng: p.coords.longitude }),
    accuracy_m: clamp(Math.round(Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : 0), 0, 100_000),
    measured_at: Math.round(p.timestamp),
  }
  const speed = p.coords.speed
  if (speed !== null && Number.isFinite(speed) && speed >= 0) fix.speed_mps = Math.min(100, speed)
  return fix
}

/** "카카오맵으로", "네이버지도로": 으로 after a final consonant other than ㄹ. */
export function withRo(word: string): string {
  const last = word.charCodeAt(word.length - 1) - 0xac00
  if (last < 0 || last > 11171) return `${word}로`
  const final = last % 28
  return `${word}${final === 0 || final === 8 ? '로' : '으로'}`
}
